import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { Subscription } from "rxjs";
import { WorkflowService } from "../../service/workflow.service";
import { WfTemplate } from "./s09-workflow.models";

/** Merge catalogue from Rezurock HTML MERGE_FIELDS (sample values for preview). */
export const MERGE_FIELDS: Record<
  string,
  { sample: string; phi: boolean }
> = {
  "Office name": { sample: "Northside Transplant Center", phi: false },
  "Office contact": { sample: "Dana", phi: false },
  "Case ID": { sample: "DU-RZ-1042", phi: false },
  "FRM name": { sample: "Alex Morgan", phi: false },
  "Hub/SP name": { sample: "AssistRx", phi: false },
  phone: { sample: "(800) 555-0142", phi: false },
  "SP contact": { sample: "Priya at AssistRx", phi: false },
  date: { sample: "Oct 6", phi: false },
  "MyRock Assist website": { sample: "myrockassist.example", phi: false },
  "Status in plain words": {
    sample: "waiting on enrollment information",
    phi: false,
  },
  "Days in status": { sample: "6", phi: false },
  "Denial reason": { sample: "missing clinicals", phi: false },
  "Days left": { sample: "9", phi: false },
  "Patient ref": { sample: "J. Rivera", phi: true },
  "Chased party": { sample: "J. Rivera", phi: true },
  "Patient name": { sample: "J. Rivera", phi: true },
};

@Component({
  selector: "app-s09-templates",
  templateUrl: "./s09-templates.component.html",
  styleUrls: ["./s09-workflow.scss"],
  standalone: false,
})
export class S09TemplatesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() brandKey: number | null = 200104;
  @ViewChild("bodyArea") bodyArea?: ElementRef<HTMLTextAreaElement>;

  items: WfTemplate[] = [];
  loading = false;
  saving = false;
  selectedCode: string | null = null;
  draft: WfTemplate = this.emptyDraft();
  hint = "";
  filter = "";
  dirty = false;
  private loadedOnce = false;

  /** Preview controls (match HTML pv-phi / pv-chase). */
  previewPhi = false;
  previewChase: "patient" | "office" = "patient";

  readonly kinds = ["Email", "Call brief", "FRM prompt"];
  readonly mergeKeys = Object.keys(MERGE_FIELDS);

  private subs: Subscription[] = [];

  constructor(private workflowService: WorkflowService) {}

  ngOnChanges(ch: SimpleChanges): void {
    if (ch["brandKey"]) {
      this.loadedOnce = true;
      this.reload();
    }
  }

  ngOnInit(): void {
    if (!this.loadedOnce) this.reload();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  get isEmail(): boolean {
    return (this.draft.kind || "Email") === "Email";
  }

  get filtered(): WfTemplate[] {
    const q = this.filter.trim().toLowerCase();
    if (!q) return this.items;
    return this.items.filter((t) => {
      const hay = `${t.template_code} ${t.template_name} ${t.kind}`.toLowerCase();
      return hay.includes(q);
    });
  }

  get previewSubject(): string {
    if (!this.isEmail) return "";
    return this.applyMerge(this.draft.subject || "");
  }

  get previewBody(): string {
    return this.applyMerge(this.draft.body || "");
  }

  get previewTo(): string {
    return `${MERGE_FIELDS["Office contact"].sample} · ${MERGE_FIELDS["Office name"].sample}`;
  }

  get previewCc(): string {
    return MERGE_FIELDS["SP contact"].sample;
  }

  reload(): void {
    const brand = Number(this.brandKey) || 0;
    if (!brand) {
      this.items = [];
      this.hint = "No brand selected.";
      return;
    }
    this.loading = true;
    this.hint = "";
    const sub = this.workflowService.getTemplates(brand).subscribe((rows) => {
      this.loading = false;
      this.items = rows || [];
      if (!this.items.length) {
        this.hint =
          "No templates for this brand. Run 07_create_template_config.sql and 08_seed_templates.sql.";
      }
      if (this.selectedCode) {
        const found = this.items.find(
          (t) => t.template_code === this.selectedCode
        );
        if (found) this.select(found);
        else this.newTemplate();
      } else if (this.items.length) {
        this.select(this.items[0]);
      } else {
        this.newTemplate();
      }
    });
    this.subs.push(sub);
  }

  select(t: WfTemplate): void {
    this.selectedCode = t.template_code;
    this.draft = {
      ...t,
      subject: t.subject || "",
      body: t.body || "",
      source_status: t.source_status || "draft",
      kind: t.kind || "Email",
    };
    this.dirty = false;
    this.hint = "";
  }

  newTemplate(): void {
    this.selectedCode = null;
    this.draft = this.emptyDraft();
    this.dirty = false;
    this.hint = "New template — set a code, then Save.";
  }

  /** Kind: Email shows subject; Call brief / FRM prompt hide subject. */
  onKindChange(): void {
    this.dirty = true;
    if (!this.isEmail) {
      // Subject is unused for non-email kinds (HTML hides the field).
      this.hint =
        this.draft.kind === "Call brief"
          ? "Call brief — subject hidden; body is the talking points."
          : "FRM prompt — subject hidden; body is the in-app prompt.";
    } else {
      this.hint = "Email — subject is shown in preview (no PHI in subject).";
    }
  }

  /** Source/Approved: doc = approved process-map copy; draft = needs approval. */
  onSourceChange(): void {
    this.dirty = true;
    this.hint =
      this.draft.source_status === "doc"
        ? "Marked as approved (doc) copy."
        : "Draft copy — needs approval before go-live.";
  }

  onFieldEdit(): void {
    this.dirty = true;
  }

  isPhiField(key: string): boolean {
    return !!MERGE_FIELDS[key]?.phi;
  }

  /** Insert [Field] into body at caret (HTML data-ins chips). */
  insertMerge(key: string): void {
    const token = `[${key}]`;
    const el = this.bodyArea?.nativeElement;
    const body = this.draft.body || "";
    if (!el) {
      this.draft.body = body + token;
      this.dirty = true;
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? start;
    this.draft.body = body.slice(0, start) + token + body.slice(end);
    this.dirty = true;
    setTimeout(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  save(): void {
    const brand = Number(this.brandKey) || 0;
    const code = (this.draft.template_code || "").trim();
    if (!brand || !code) {
      this.hint = "Template code is required.";
      return;
    }
    // Non-email kinds do not use subject in the HTML UI — clear on save for consistency.
    const subject = this.isEmail ? this.draft.subject || "" : "";
    this.saving = true;
    const sub = this.workflowService
      .saveTemplate({
        brand_key: brand,
        template_code: code,
        template_name: (this.draft.template_name || "").trim() || code,
        kind: this.draft.kind || "Email",
        source_status: this.draft.source_status || "draft",
        subject,
        body: this.draft.body || "",
        sort_order: this.draft.sort_order,
        is_active: true,
      })
      .subscribe((saved) => {
        this.saving = false;
        if (!saved) {
          this.hint =
            "Save failed. Confirm the API is running and scripts 07/08 were applied for this brand.";
          return;
        }
        this.selectedCode = saved.template_code;
        this.dirty = false;
        this.hint = `Saved ${saved.template_code}.`;
        this.reload();
      });
    this.subs.push(sub);
  }

  remove(): void {
    const brand = Number(this.brandKey) || 0;
    const code = this.selectedCode || this.draft.template_code;
    if (!brand || !code) return;
    if (!confirm(`Delete template ${code}?`)) return;
    const sub = this.workflowService
      .deleteTemplate(brand, code)
      .subscribe((ok) => {
        if (ok) {
          this.selectedCode = null;
          this.reload();
        }
      });
    this.subs.push(sub);
  }

  sourceLabel(t: WfTemplate): string {
    return t.source_status === "doc"
      ? "from process-map doc"
      : "draft copy, needs approval";
  }

  private emptyDraft(): WfTemplate {
    return {
      template_code: "",
      template_name: "",
      kind: "Email",
      source_status: "draft",
      subject: "",
      body: "",
      sort_order: 0,
    };
  }

  private applyMerge(text: string): string {
    return String(text || "").replace(/\[([^\]]+)\]/g, (_, key: string) => {
      const k = String(key || "").trim();
      const resolved = this.resolveField(k);
      return resolved != null ? resolved : `[${k}]`;
    });
  }

  /** Matches HTML resolveField(name, {phi, chase}). */
  private resolveField(name: string): string | null {
    const caseId = MERGE_FIELDS["Case ID"].sample;
    const patientName = MERGE_FIELDS["Patient name"].sample;
    if (name === "Patient ref") {
      return this.previewPhi
        ? patientName
        : `your patient (ref ${caseId})`;
    }
    if (name === "Chased party") {
      if (this.previewChase === "office") return "the office";
      return this.previewPhi
        ? patientName
        : `your patient (ref ${caseId})`;
    }
    if (name === "Patient name") {
      return this.previewPhi
        ? patientName
        : "[name withheld — no PHI consent]";
    }
    const def = MERGE_FIELDS[name];
    return def ? def.sample : null;
  }
}
